import ShareIcon from "@mui/icons-material/Share";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { postPdb } from "../../api/ApiService";
import { useSummary } from "../../hooks/useSummary";
import { ProductType } from "../../shared/enums/ProductType";
import { WizardStep } from "../../shared/enums/WizardStep";
import { cartHelper } from "../../shared/helpers/cartHelper";
import { linkHelper } from "../../shared/helpers/linkHelper";
import { useMarket } from "../../store/hooks/useMarket";
import { CartOptional, CartProduct } from "../../store/interfaces/Cart";
import ListTable, { ListTableMode } from "../shared/ListTable/ListTable";
import ProductListItem from "./ProductItem";
import "./ProductsList.scss";
import ShareDialog from "./ShareDialog";
import { routesConfig } from "../../config/routes";
import { cookies } from "../../shared/helpers/cookies";

interface ProductsListProps {
  cartOptional: CartOptional;
  cartProduct: CartProduct;
  isLoadedFromSummary: boolean;
}

const ProductsList = ({
  cartOptional,
  cartProduct,
  isLoadedFromSummary,
}: ProductsListProps) => {
  const { t } = useTranslation(["yourchoises", "contactusform"]);
  const [showShareDialog, setShowShareDialog] = useState<boolean>(false);
  const [summaryLink, setSummaryLink] = useState<string>(undefined);
  let selectedMarket = "";
  const flagMarket = cookies.getFlagMarket();
  const market = useMarket();
  if (flagMarket !== "") {
    selectedMarket = flagMarket;
  } else {
    selectedMarket = market;
  }
  const { getSummary } = useSummary();
  const [counter, setCounter] = useState<number>(0);

  const productLink = linkHelper.link(WizardStep.Product, market);
  const optionalLink = linkHelper.link(WizardStep.Optional, market);

  useEffect(() => {
    const count = cartHelper.countSelectedProducts({
      cartOptional,
      cartProduct,
    });
    setCounter(count);
  }, [cartProduct, cartOptional]);

  const onShareClick = () => {
    setShowShareDialog(true);
    const pdbBody = getSummary();

    postPdb("api/summary/", pdbBody).then((result) => {
      let pdbmarket = selectedMarket;
      if(selectedMarket === "fr-BE" || selectedMarket === "en-CA") {
        pdbmarket = "fr-FR";
      }
      if(selectedMarket === "nl-BE") {
        pdbmarket = "nl-NL";
      }
      const link = `${process.env.REACT_APP_URLPATH}${routesConfig[pdbmarket].contactUsStep}?id=${result.id}`;
      //const link = `${window.location.href}?id=${result.id}`;
      setSummaryLink(link);
    });
  };

  const onShareCloseClick = () => {
    setShowShareDialog(false);
    setSummaryLink(undefined);
  };

  return (
    <div className="productsContainer">
      <ShareDialog
        onClose={onShareCloseClick}
        link={summaryLink}
        openDialog={showShareDialog}
      />
      {!isLoadedFromSummary && (
        <div className="productsHeader">
          <h2>{t("reviewchoises")}</h2>
          <div className="productsHeaderShare" onClick={onShareClick}>
            <ShareIcon style={{ marginRight: "8px" }} />
            {t("share.share", { ns: "contactusform" })}
          </div>
        </div>
      )}
      <div className="listItemsContainer">
        <h3 className="">{`${counter} ${t("products")}`}</h3>
        <ListTable mode={ListTableMode.List}>
          {cartProduct?.tiltrotator && (
            <ProductListItem
              configLink={productLink}
              market={market}
              product={cartProduct?.tiltrotator}
              productType={ProductType.Rototilts}
            />
          )}
          {cartProduct?.machineFixing && (
            <ProductListItem
              configLink={productLink}
              market={market}
              product={cartProduct?.machineFixing}
              productType={ProductType.MachineFixings}
            />
          )}
          {cartProduct?.upperBracket && (
            <ProductListItem
              configLink={productLink}
              market={market}
              product={cartProduct?.upperBracket}
              productType={ProductType.UpperBrackets}
            />
          )}
          {cartProduct?.lowerBracket && (
            <ProductListItem
              configLink={productLink}
              market={market}
              product={cartProduct?.lowerBracket}
              productType={ProductType.LowerBrackets}
            />
          )}
          {cartProduct?.grappleModule && (
            <ProductListItem
              configLink={productLink}
              market={market}
              product={cartProduct?.grappleModule}
              productType={ProductType.GrappleModules}
            />
          )}
          {cartProduct?.toolsProductItems &&
            cartProduct?.toolsProductItems.map((toolsProductItem) => (
              <ProductListItem
                configLink={productLink}
                key={toolsProductItem.id}
                market={market}
                product={toolsProductItem}
              />
            ))}
          {cartOptional?.tiltrotator && (
            <ProductListItem
              configLink={optionalLink}
              market={market}
              product={cartOptional?.tiltrotator}
              productType={ProductType.Rototilts}
            />
          )}
          {cartOptional?.grappleModule && (
            <ProductListItem
              configLink={optionalLink}
              market={market}
              product={cartOptional?.grappleModule}
              productType={ProductType.GrappleModules}
            />
          )}
          {cartOptional?.machineFixing && (
            <ProductListItem
              configLink={optionalLink}
              market={market}
              product={cartOptional?.machineFixing}
              productType={ProductType.MachineFixings}
            />
          )}
          {cartOptional?.toolsProductItems &&
            cartOptional?.toolsProductItems.map((toolsProductItem) => (
              <ProductListItem
                configLink={optionalLink}
                key={toolsProductItem.id}
                market={market}
                product={toolsProductItem}
              />
            ))}
        </ListTable>
      </div>
    </div>
  );
};

export default ProductsList;
